import React from "react";
import { useDispatch } from "react-redux";

import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { MosaicDirection } from "src/utils/MosaicTree";

// Material-UI
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, Button, Grid, Theme, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IFlight, IGroundEvent } from "@vmosolutions/oc-interfaces";
import { IconButton } from "src/components/IconButton/IconButton";

interface ITimelineAddEvents {
  id?: string;
  path?: string[];
  setFocusOnEvent?: (event: IFlight | IGroundEvent) => void;
  setTabValue: React.Dispatch<React.SetStateAction<string | boolean>>;
}

const TimelineAddEventsStyles = (theme: Theme): any => ({
  display: "flex",
  padding: "10px",
  flexWrap: "wrap",
  alignItems: "center",
  "& .build_button": { margin: "0 5px" },
});

/**
 * TIMELINE ADD EVENT TOOL
 * Displays a series of quicklinks to the flight builder component so that users can jump straight to the type of timeline event builder they want.
 */
export const TimelineAddEvents = (props: ITimelineAddEvents): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { id, path, setFocusOnEvent, setTabValue } = props;
  // Creates a mosaic Tab node and opens a new content type pane with the flight builder.
  const handleClickLink = (eventType: string) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (eventType === "UnassignedFlightsWindow") {
      // Assign Flights works on this flight display's aircraft selection. Not passed as `id`, which openWindow would
      // take as the new window's own id.
      dispatch(
        openWindow(
          "Assign Flights",
          "UnassignedFlightsWindow",
          path,
          { setFocusOnEvent: setFocusOnEvent, flightDisplayId: id },
          "context"
        )
      );
    } else if (eventType === "ImportEvents") {
      dispatch(
        openWindow(
          "Import Events",
          "ImportEventsWindow",
          path,
          { setFocusOnEvent: setFocusOnEvent },
          "context",
          true,
          MosaicDirection.Right,
          840
        )
      );
    } else {
      dispatch(openWindow("Flight Builder", "FlightBuilder", [], { eventType: eventType }, "content"));
    }
  };

  return (
    <Box sx={TimelineAddEventsStyles(theme)}>
      <Grid container>
        <Grid container style={{ width: "auto" }} alignItems="center">
          <IconButton style={{ paddingBottom: "4px" }} onClick={() => setTabValue(false)}>
            <VisibilityOffIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" className="body_text">
            Create
          </Typography>
          <Button
            id={"add_flights_btn"}
            className="build_button"
            variant="contained"
            onClick={handleClickLink("Flight")}
          >
            Flights
          </Button>
          <Button
            id={"add_recurring_flights_btn"}
            className="build_button"
            variant="contained"
            onClick={handleClickLink("RecurrentFlight")}
          >
            Recurring Flights
          </Button>
          <Button
            id={"add_ground_events_btn"}
            className="build_button"
            variant="contained"
            onClick={handleClickLink("GroundEvent")}
          >
            Ground Events
          </Button>

          <Button
            id={"import_events_btn"}
            className="build_button"
            variant="contained"
            onClick={handleClickLink("ImportEvents")}
          >
            Import Events
          </Button>
        </Grid>

        <Grid container style={{ width: "auto" }} alignItems="center">
          <Typography variant="subtitle1" className="body_text">
            Assign To Aircraft
          </Typography>
          <Button
            id={"assign_flights_btn"}
            className="build_button"
            variant="contained"
            onClick={handleClickLink("UnassignedFlightsWindow")}
          >
            Unassigned Flights
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
